import React, { useState, useEffect } from "react";
import { FaSpinner, FaMapMarkerAlt, FaBuilding, FaTimes, FaCheckCircle } from "react-icons/fa";
import { API_BASE } from "../../utils/api";
import { fetchSalesman } from "../../utils/fetchSalesman";
import { isBMI, getMyAccountId, BMI_CUSTOMER_ID, BMI_CUSTOMER_ID_GYE } from "../../utils/config";
import { fetchBudgets, fetchBudgetConsumption } from "../../utils/budgetApi";
import { MONTHS } from "../BudgetGrid";
import BudgetProgressBar from "../BudgetProgressBar";

// Bodegas SEREC (getBranches) segun el cliente BMI elegido — solo aplica a las
// dos cuentas BMI (UIO/GYE), para enrutar el pedido a la bodega correcta en vez
// de dejar la que el usuario tuviera activa antes de abrir el selector.
const SEREC_BODEGA_BY_BMI_CUSTOMER = {
    [BMI_CUSTOMER_ID_GYE]: { bodegaId: " 1", bodegaName: "GUAYAQUIL" },
    [BMI_CUSTOMER_ID]: { bodegaId: "  ", bodegaName: "QUITO" },
};

export default function Ext1CustomerSelector({ open, onSelect, onClose }) {
    const [options, setOptions] = useState([]);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [groupName, setGroupName] = useState(null);
    const [budgetStatusByKey, setBudgetStatusByKey] = useState({});

    useEffect(() => {
        if (!open) return;
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user?.idEmployee) return;

        setLoading(true);
        setSelected(null);
        setGroupName(null);
        if (user.valorExt1 > 0) {
            fetch(`${API_BASE}/api-ultra/api/ws/get_group_name?id_group=${user.valorExt1}`)
                .then(r => r.ok ? r.text() : null)
                .then(name => { if (name) setGroupName(name); })
                .catch(() => {});
        }

        fetch(`${API_BASE}/api-ultra/api/ws/parameters_cust_ship?id_employee=${user.idEmployee}&myaccount_id=${user.myaccountId}&has_ship_to=${user.hasCustShipTo ?? false}&id_customer=${user.idCustomer ?? 0}`)
            .then((r) => r.json())
            .then((data) => setOptions(Array.isArray(data) ? data : []))
            .catch((err) => console.error("Error cargando ext1", err))
            .finally(() => setLoading(false));
    }, [open]);

    // Estado de presupuesto (asignado vs consumo real del mes) por cada opcion BMI,
    // para verlo de un vistazo al elegir sucursal. Solo informativo.
    useEffect(() => {
        if (!options.length) return;
        const bmiOptions = options.filter((o) => isBMI({ idCustomer: o.idCustomer }));
        if (!bmiOptions.length) return;

        const MYACCOUNT_ID = getMyAccountId();
        const currentMonthKey = MONTHS[new Date().getMonth()].key;
        const currentMonth = new Date().getMonth() + 1;
        const currentYear = new Date().getFullYear();

        Promise.all(
            bmiOptions.map((o) => {
                const idShipTo = o.idShipTo || 0;
                return Promise.all([
                    fetchBudgets({ myaccountId: MYACCOUNT_ID, idCustomer: o.idCustomer, idShipTo }),
                    fetchBudgetConsumption({ idCustomer: o.idCustomer, idShipTo }),
                ]).then(([budgetRows, consumptionRows]) => {
                    const asignado = (budgetRows || []).reduce(
                        (sum, r) => sum + (Number(r[currentMonthKey]) || 0),
                        0
                    );
                    const consumo = (consumptionRows || [])
                        .filter((r) => r.year1 === currentYear && r.month1 === currentMonth)
                        .reduce((sum, r) => sum + (Number(r.totalPrice) || 0), 0);
                    const pct = asignado > 0 ? (consumo / asignado) * 100 : 0;
                    return { key: `${o.idCustomer}-${idShipTo}`, asignado, pct };
                });
            })
        )
            .then((results) => {
                const map = {};
                results.forEach((r) => {
                    map[r.key] = r;
                });
                setBudgetStatusByKey(map);
            })
            .catch((err) => console.error("Error cargando estado de presupuesto", err));
    }, [options]);

    if (!open) return null;

    const handleConfirm = async () => {
        if (!selected) return;
        setConfirming(true);
        try {
            const res = await fetch(
                `${API_BASE}/customers-ultra/api/ws/func_returnCustomer?id_customer=${selected.idCustomer}&id_ship_to=${selected.idShipTo || 0}`
            );
            if (!res.ok) throw new Error("Error al obtener detalle del cliente");
            const fullCustomer = await res.json();

            const bodega = SEREC_BODEGA_BY_BMI_CUSTOMER[selected.idCustomer];
            if (bodega) {
                Object.assign(fullCustomer, bodega);
            }

            if (fullCustomer.idSalesman) {
                const newSalesman = await fetchSalesman(fullCustomer.idSalesman);
                if (newSalesman) {
                    localStorage.setItem("salesman", JSON.stringify(newSalesman));
                    window.dispatchEvent(new CustomEvent("salesman-updated"));
                }
            }

            onSelect(fullCustomer);
        } catch (err) {
            console.error("Error en func_returnCustomer", err);
            alert("No se pudo obtener la información del cliente.");
        } finally {
            setConfirming(false);
        }
    };


    return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/40">
            <div className="bg-white w-full max-w-xl rounded-2xl shadow-2xl overflow-hidden">

                {/* Header */}
                <div className="px-6 pt-5 pb-4 flex items-center justify-between border-b border-gray-100">
                    <div className="flex items-center gap-2">
                        <FaBuilding className="text-gray-400 text-sm" />
                        <h3 className="text-gray-800 font-bold text-base">
                            {groupName ?? "Seleccione una Dirección"}
                        </h3>
                    </div>
                    {onClose && (
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition rounded-full p-1 hover:bg-gray-100"
                        >
                            <FaTimes size={15} />
                        </button>
                    )}
                </div>

                {/* Body */}
                <div className="p-6">
                    {loading ? (
                        <div className="flex flex-col items-center py-10 gap-3 text-gray-400">
                            <FaSpinner className="animate-spin text-brand-primary text-2xl" />
                            <span className="text-sm">Cargando...</span>
                        </div>
                    ) : options.length === 0 ? (
                        <div className="text-center py-10 text-gray-400 text-sm">
                            No se encontraron nada...
                        </div>
                    ) : (
                        <>
                            <div className="max-h-[340px] overflow-y-auto pr-1 space-y-2">
                                {options.map((item, idx) => {
                                    const key = `${item.idCustomer}-${item.idShipTo ?? "null"}-${idx}`;
                                    const isSelected =
                                        selected?.idCustomer === item.idCustomer &&
                                        selected?.idShipTo === item.idShipTo;
                                    const isShipTo = !!item.idShipTo;
                                    const budgetInfo = budgetStatusByKey[`${item.idCustomer}-${item.idShipTo || 0}`];

                                    return (
                                        <label
                                            key={key}
                                            className={`flex items-center gap-3 px-4 py-3 border-2 rounded-xl cursor-pointer transition-all ${
                                                isSelected
                                                    ? "border-brand-primary bg-brand-primary/10 shadow-sm"
                                                    : isShipTo
                                                    ? "ml-6 border border-gray-200 bg-gray-50 hover:border-gray-300"
                                                    : "border-gray-200 hover:border-brand-primary hover:shadow-sm"
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                className="accent-brand-primary"
                                                checked={isSelected}
                                                onChange={() => setSelected(item)}
                                            />
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    {isShipTo
                                                        ? <FaMapMarkerAlt className="text-brand-primary text-xs flex-shrink-0" />
                                                        : <FaBuilding className="text-gray-400 text-xs flex-shrink-0" />
                                                    }
                                                    <span className={`font-semibold truncate ${isShipTo ? "text-gray-600 text-sm" : "text-gray-800 text-sm"}`}>
                                                        {isShipTo ? item.nameShipTo : item.companyName}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-gray-400 mt-0.5 pl-4">
                                                    {isShipTo ? item.companyName : ``}
                                                </p>
                                            </div>
                                            {budgetInfo && budgetInfo.asignado > 0 && (
                                                <div className="flex-shrink-0" title="Presupuesto del mes">
                                                    <BudgetProgressBar asignado={budgetInfo.asignado} pct={budgetInfo.pct} />
                                                </div>
                                            )}
                                            {isSelected && (
                                                <FaCheckCircle className="text-brand-primary text-base flex-shrink-0" />
                                            )}
                                        </label>
                                    );
                                })}
                            </div>

                            <div className="flex justify-center gap-3 mt-6 pt-4 border-t border-gray-100">
                                {onClose && (
                                    <button
                                        onClick={onClose}
                                        disabled={confirming}
                                        className="px-5 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg disabled:opacity-50 transition"
                                    >
                                        Cancelar
                                    </button>
                                )}
                                <button
                                    onClick={handleConfirm}
                                    disabled={!selected || confirming}
                                    className="px-6 py-2 text-sm bg-brand-primary text-white rounded-lg font-semibold disabled:opacity-50 flex items-center gap-2 hover:opacity-90 transition"
                                >
                                    {confirming && <FaSpinner className="animate-spin" size={13} />}
                                    Confirmar
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
